import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "../lib/apiClient";

export default function PaymentResult({ mode = "success" }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(mode === "cancel" ? "canceled" : "checking");
  const tries = useRef(0);

  useEffect(() => {
    if (mode === "cancel" || !sessionId) return;
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (stop) return;
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("failed"); return; }
      } catch (_) {}
      tries.current += 1;
      if (tries.current > 20) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId, mode]);

  const config = {
    checking: { icon: Loader2, spin: true, color: "var(--accent)", title: "Validation du paiement…", sub: "Un instant, Sara confirme ton abonnement.", cta: null },
    paid: { icon: CheckCircle2, color: "#00ffaa", title: "Abonnement activé !", sub: "Merci — ton offre est désormais active. Bienvenue à bord.", cta: "Aller à mon compte" },
    canceled: { icon: XCircle, color: "#ffab00", title: "Paiement annulé", sub: "Aucun montant n'a été prélevé. Tu peux réessayer quand tu veux.", cta: "Revenir aux offres" },
    failed: { icon: XCircle, color: "#ff6b6b", title: "Paiement échoué", sub: "Le paiement n'a pas abouti. Réessaie ou change de moyen de paiement.", cta: "Revenir aux offres" },
    timeout: { icon: Loader2, color: "var(--accent)", title: "Toujours en cours…", sub: "La confirmation prend un peu de temps. Vérifie ton compte dans un instant.", cta: "Aller à mon compte" },
  }[state];

  const Icon = config.icon;
  return (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid="payment-result">
      <div className="stage-glow" />
      <div className="relative z-10 hud-panel clip-corner max-w-md w-full p-8 text-center">
        <span
          className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full"
          style={{ background: `${config.color}1a`, border: `1px solid ${config.color}` }}
        >
          <Icon className={`h-8 w-8 ${config.spin ? "animate-spin" : ""}`} style={{ color: config.color }} />
        </span>
        <h1 className="font-display text-2xl font-black tracking-[0.08em] text-white" data-testid="payment-result-title">{config.title}</h1>
        <p className="mt-2 text-sm text-[#9fb2c6]">{config.sub}</p>
        {config.cta && (
          <button
            type="button"
            data-testid="payment-result-cta"
            onClick={() => navigate("/compte")}
            className="hud-btn clip-corner mt-6 w-full py-3 text-sm font-bold"
          >
            {config.cta}
          </button>
        )}
      </div>
    </div>
  );
}
